import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Collapse,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { IconArrowUndoUp } from 'central-icons/IconArrowUndoUp';
import { useState } from 'react';

import styles from './ConversationView.module.css';

const SparkleIcon = ({ size = 24 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
      fill="white"
    />
  </svg>
);

interface AIResponseMessageProps {
  content: string;
  thinking?: string;
  isGenerating?: boolean;
  onRestore?: () => void;
  showRestore?: boolean;
}

function getThinkingButtonText(
  isGenerating: boolean,
  isThinkingExpanded: boolean
): string {
  if (isGenerating) {
    return 'Thinking';
  }
  return isThinkingExpanded ? 'Hide Thinking Process' : 'Show Thinking Process';
}

export function AIResponseMessage({
  content,
  thinking,
  isGenerating = false,
  onRestore,
  showRestore = false,
}: AIResponseMessageProps) {
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(false);

  return (
    <Flex
      justify="flex-start"
      align="flex-start"
      gap={2}
      mb={4}
      position="relative"
      _hover={{
        '& .restore-btn': { opacity: 1 },
      }}
    >
      <Box
        width="32px"
        height="32px"
        borderRadius="8px"
        bgGradient="linear(to-br, #2674D1, #5AD0D0, #9ADF7A)"
        flexShrink={0}
        position="relative"
      >
        <Box position="absolute" left="6px" top="13px">
          <SparkleIcon size={17} />
        </Box>
        <Box position="absolute" right="6px" top="8px">
          <SparkleIcon size={13} />
        </Box>
      </Box>
      <Box maxW="calc(80% - 40px)" flex={1} minW={0} position="relative">
        <Text fontSize="sm" color="gray.700" lineHeight="tall">
          {content}
          {showRestore && onRestore && (
            <Box
              as="span"
              display="inline-block"
              w={6}
              h="1em"
              ml={0.5}
              verticalAlign="bottom"
              aria-hidden
            />
          )}
        </Text>
        {showRestore && onRestore && (
          <Tooltip
            label="Revert to this dashboard state"
            placement="top"
            openDelay={200}
          >
            <Box
              as="span"
              position="absolute"
              bottom={0}
              right={0}
              display="inline-block"
            >
              <IconButton
                className="restore-btn"
                aria-label="Revert to this configuration"
                icon={<IconArrowUndoUp size={16} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={onRestore}
                minW="auto"
                h="auto"
                p={1}
                opacity={0}
                transition="opacity 0.2s"
              />
            </Box>
          </Tooltip>
        )}
        {(isGenerating || (thinking && thinking.length > 0)) && (
          <Box mt={1}>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gray"
              onClick={() => setIsThinkingExpanded(!isThinkingExpanded)}
              rightIcon={
                isThinkingExpanded ? (
                  <ChevronUpIcon boxSize={3} />
                ) : (
                  <ChevronDownIcon boxSize={3} />
                )
              }
              mb={0}
              className={isGenerating ? styles.thinkingRow : undefined}
            >
              {getThinkingButtonText(isGenerating, isThinkingExpanded)}
            </Button>
            {thinking && thinking.length > 0 && (
              <Collapse in={isThinkingExpanded} animateOpacity>
                <Box
                  borderRadius="sm"
                  p={2}
                  mt={0}
                  maxH="300px"
                  overflowY="auto"
                  className={styles.thinkingContent}
                >
                  <Text
                    fontFamily="mono"
                    fontSize="xs"
                    color="gray.700"
                    whiteSpace="pre-wrap"
                    lineHeight="tall"
                    wordBreak="break-word"
                  >
                    {thinking}
                  </Text>
                </Box>
              </Collapse>
            )}
          </Box>
        )}
      </Box>
    </Flex>
  );
}
